import { clamp, type Vec3 } from './math.js';
import type { MovementState } from './types.js';
import type { Weapon } from './weapons.js';

/**
 * Weapon bloom / spread — the CS/Valorant-style accuracy model.
 *
 * `bloom` is a normalized 0..1 accumulator: movement sets an instant FLOOR
 * (you cannot out-run your own inaccuracy), firing ADDS on top, and it recovers
 * exponentially back toward the floor when you stop firing.
 *
 * This is a PURE function of state the client and server both have (movement +
 * weapon), so both compute the same cone. The client shows the cone as the
 * crosshair gap; the server perturbs the actual bullet inside the cone with its
 * OWN rng (perturbDir) — the only non-shared piece, which keeps it fair.
 */
export interface SpreadState {
  bloom: number;
}

export function freshSpread(): SpreadState {
  return { bloom: 0 };
}

/**
 * Advance bloom by one tick. `dt` in seconds. Returns a new state.
 * `suppression` (0..1) is how suppressed the player is — incoming near-misses
 * raise the bloom floor, so a pinned player physically cannot aim tightly.
 */
export function advanceSpread(
  s: SpreadState,
  m: MovementState,
  firedThisTick: boolean,
  w: Weapon,
  dt: number,
  suppression = 0,
): SpreadState {
  const speed = Math.hypot(m.vel.x, m.vel.z);
  const moveFloor = Math.max(
    (!m.onGround
      ? w.bloomAir
      : speed > 0.1
        ? Math.min(1, speed / w.bloomRefSpeed) * w.bloomMove
        : 0) * (m.crouching ? w.crouchMul : 1),
    suppression * 0.75, // being suppressed sets its own accuracy floor
  );

  let bloom = Math.max(s.bloom, moveFloor); // movement raises the floor instantly
  if (firedThisTick) bloom = Math.min(1, bloom + w.bloomPerShot);

  // Exponential recovery of the excess above the floor.
  const rec = 1 - Math.exp(-dt * w.recoverRate);
  bloom = moveFloor + (bloom - moveFloor) * (1 - rec);

  return { bloom: clamp(bloom, 0, 1) };
}

/** Current cone half-angle in radians. ADS tightens the cone per weapon. */
export function spreadAngle(s: SpreadState, w: Weapon, ads = false): number {
  const cone = w.baseSpread + (w.maxSpread - w.baseSpread) * s.bloom;
  return ads ? cone * w.adsSpreadMul : cone;
}

/**
 * Perturb a unit direction by a random offset uniformly within a cone of the
 * given half-angle. SERVER-ONLY caller (uses an unpredictable rng). `rng`
 * returns [0,1).
 */
export function perturbDir(dir: Vec3, halfAngle: number, rng: () => number): Vec3 {
  if (halfAngle <= 1e-6) return { x: dir.x, y: dir.y, z: dir.z };

  // Build an orthonormal basis (t, b) perpendicular to dir.
  const up = Math.abs(dir.y) < 0.99 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const t = normalize(cross(up, dir));
  const b = cross(dir, t);

  const az = rng() * 2 * Math.PI;
  // Uniform sampling on the spherical cap.
  const polar = Math.acos(1 - rng() * (1 - Math.cos(halfAngle)));
  const sp = Math.sin(polar);
  const cp = Math.cos(polar);
  const cosA = Math.cos(az);
  const sinA = Math.sin(az);

  return normalize({
    x: dir.x * cp + (t.x * cosA + b.x * sinA) * sp,
    y: dir.y * cp + (t.y * cosA + b.y * sinA) * sp,
    z: dir.z * cp + (t.z * cosA + b.z * sinA) * sp,
  });
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

function normalize(v: Vec3): Vec3 {
  const len = Math.hypot(v.x, v.y, v.z) || 1;
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}
